import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import AutomationHowItWorks from './AutomationHowItWorks';
import AutomationSection from './AutomationSection';
import BlockListRule from './BlockListRule';
import BlockedBuyersCard from './BlockedBuyersCard';
import Callout from '../../shared/components/Callout';
import IssuerAlertCard from './IssuerAlertCard';
import RailGroup from './RailGroup';
import RuleCard from './RuleCard';
import SignalActionCard from './SignalActionCard';
import SignalRule from './SignalRule';
import client from '../../../shared/api/client';
import { toastError, toastSuccess } from '../../../shared/toastHelper';
import {
  BLOCK_LIST_COPY,
  DIGEST_COPY,
  PAGE_COPY,
  SECTIONS,
  SIGNAL_RULES,
  SIGNAL_RULE_TYPES,
} from '../constants/automationRules';

// The page reads as one statement rather than a flat list of rules: the card issuer's own
// alerts stand apart (the bank confirmed the fraud, we didn't guess it), the Opendate signals
// are detectors feeding a single action, and what happens to a blocked buyer is its own
// concern that outlives the rule which created it.
//
// Values AND their allowed options both come from the server
// (FinanceAutomationRule.effective_row), so this file hardcodes no threshold and no option
// list — the previous version kept its own copy of CONFIG_OPTIONS and had already drifted.
const AutomationRules = ({
  team,
  canAdminister,
  rulesAvailable,
  automationEnabled,
  autoRefundAvailable,
  signalAction,
  digest,
  onDigestChange,
  onBack,
}) => {
  const [rules, setRules] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [savingType, setSavingType] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);

  useEffect(() => {
    let live = true;
    client
      .get(`/teams/${team.id}/finance_automation_rules.json`)
      .then((res) => {
        if (!live) return;
        const byType = {};
        (res.data.rules || []).forEach((rule) => {
          byType[rule.rule_type] = rule;
        });
        setRules(byType);
      })
      .catch(() => {
        if (live) setLoadError(true);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [team.id]);

  const ruleFor = (ruleType) =>
    rules[ruleType] || { rule_type: ruleType, enabled: false, config: {}, options: {} };

  const save = useCallback(
    (ruleType, next, successMsg) => {
      // ruleFor() supplies the same default row the card is rendering when nothing is saved
      // yet, so a failed save restores what the user was looking at. Reading rules[ruleType]
      // directly would write undefined back and blank the card instead.
      const prev = ruleFor(ruleType);
      setRules((current) => ({ ...current, [ruleType]: next }));
      setSavingType(ruleType);
      client
        .patch(`/teams/${team.id}/finance_automation_rules/${ruleType}`, {
          rule: { enabled: next.enabled, config: next.config },
        })
        .then((res) => {
          setRules((current) => ({ ...current, [ruleType]: res.data }));
          if (successMsg) toastSuccess(successMsg);
        })
        .catch((error) => {
          setRules((current) => ({ ...current, [ruleType]: prev }));
          // A 403 is the write gate (FinanceAutomationRule.writable?), not a transient
          // failure: this tab was loaded while the team's rollout flag was on and it has since
          // been turned off. "Please try again" would invite an infinite retry.
          const denied = error?.response?.status === 403;
          toastError(denied ? PAGE_COPY.saveDenied : PAGE_COPY.saveError);
        })
        .finally(() => setSavingType(null));
    },
    [team.id, rules],
  );

  // The digest is a team_memberships column, not a rule, so it has its own endpoint — and its
  // subscribed state is held by the parent so it survives leaving and re-entering this view.
  // Never gated: a personal email preference has nothing to do with the rollout flag.
  const toggleDigest = (on) => {
    if (!digest.membershipId) return;
    onDigestChange(on);
    setDigestBusy(true);
    client
      .patch(`/teams/${team.id}/team_memberships/${digest.membershipId}`, {
        team_membership: { send_daily_risk_digest: on },
      })
      .then(() => toastSuccess(`${DIGEST_COPY.title}: ${on ? 'on' : 'off'}.`))
      .catch(() => {
        onDigestChange(!on);
        toastError(PAGE_COPY.saveError);
      })
      .finally(() => setDigestBusy(false));
  };

  // Two gates, and the server does NOT treat them alike — so they must not collapse into one
  // boolean here. FinanceAutomationRule.writable? needs BOTH (that is the write path), but
  // FraudRuleEvaluator#route! and Order#evaluate_fraud_rules! check ONLY the team flag,
  // deliberately (there is a test pinning it). So pulling the org-wide kill switch stops a
  // team ARMING a rule; it does not stop an already-armed rule FIRING.
  //
  //   notYet — this team has never had automation. Nothing is running, nothing can be
  //            armed: show "Coming soon" instead of a switch, and the release notice.
  //   frozen — the kill switch is off but this team is still flagged. Their armed rules
  //            ARE firing, so the switch stays rendered (disabled). Hiding it behind a
  //            Coming soon badge would tell them nothing is running while it is, and leave
  //            them no way to see which rules are live.
  //
  // Only the signal rules are gated, and they are all gated together — block_list_on_refund
  // (the one ungated rule) renders through BlockListRule, which never asks.
  const notYet = !automationEnabled;
  const frozen = automationEnabled && !rulesAvailable;

  const toggle = (ruleType, title, on) => {
    const current = ruleFor(ruleType);
    save(ruleType, { ...current, enabled: on }, `${title} ${on ? 'on' : 'off'}.`);
  };

  const changeConfig = (ruleType, key, value) => {
    const current = ruleFor(ruleType);
    save(ruleType, { ...current, config: { ...current.config, [key]: value } });
  };

  const enabledSignalCount = SIGNAL_RULE_TYPES.filter((type) => rules[type]?.enabled).length;
  const blockList = ruleFor(BLOCK_LIST_COPY.ruleType);

  // Only the rule sections wait on the fetch. The header, the release notice, the model
  // explainer and the digest own no rule state, so holding them back would leave the whole
  // page blank for a round trip it doesn't depend on.
  const rulesBody = () => {
    if (loading) return <div className="automation-rules__status">{PAGE_COPY.loading}</div>;
    if (loadError) return <div className="automation-rules__status">{PAGE_COPY.loadError}</div>;

    return (
      <>
        <AutomationSection section={SECTIONS.issuer}>
          <IssuerAlertCard autoRefundAvailable={autoRefundAvailable} />
        </AutomationSection>

        <AutomationSection section={SECTIONS.signals}>
          <div className="automation-rules__rail">
            <RailGroup
              pill="IF"
              caption="an order trips the patterns you've turned on"
              groupId="signals-if"
            >
              {SIGNAL_RULES.map((copy) => (
                <SignalRule
                  key={copy.ruleType}
                  copy={copy}
                  rule={ruleFor(copy.ruleType)}
                  deferred={notYet}
                  locked={frozen}
                  busy={savingType === copy.ruleType}
                  canAdminister={canAdminister}
                  onToggle={(on) => toggle(copy.ruleType, copy.title, on)}
                  onConfigChange={(key, value) => changeConfig(copy.ruleType, key, value)}
                />
              ))}
            </RailGroup>
            <RailGroup
              pill="THEN"
              caption="this is what Opendate does about it"
              groupId="signals-then"
            >
              <SignalActionCard
                enabledSignalCount={enabledSignalCount}
                totalSignalCount={SIGNAL_RULE_TYPES.length}
                deferred={notYet}
                autoRefundAvailable={autoRefundAvailable}
                signalAction={signalAction}
                onReviewFlagged={onBack}
              />
            </RailGroup>
          </div>
        </AutomationSection>

        <AutomationSection section={SECTIONS.afterRefund}>
          <div className="automation-rules__stack">
            <BlockListRule
              rule={blockList}
              busy={savingType === BLOCK_LIST_COPY.ruleType}
              canAdminister={canAdminister}
              onToggle={(on) => toggle(BLOCK_LIST_COPY.ruleType, BLOCK_LIST_COPY.title, on)}
            />
            <BlockedBuyersCard />
          </div>
        </AutomationSection>
      </>
    );
  };

  return (
    <div className="automation-rules">
      <button type="button" className="automation-rules__back" onClick={onBack}>
        <span aria-hidden="true">←</span> {PAGE_COPY.back}
      </button>

      <h2 className="automation-rules__title">{PAGE_COPY.title}</h2>
      <p className="automation-rules__subtitle">{PAGE_COPY.subtitle}</p>

      {notYet ? (
        <Callout tone="info" page lead={PAGE_COPY.deferredLead} testId="deferred-notice">
          {PAGE_COPY.deferredBody}
        </Callout>
      ) : null}

      <AutomationHowItWorks />

      {rulesBody()}

      <AutomationSection section={SECTIONS.notifications}>
        <RuleCard
          ruleType={DIGEST_COPY.ruleType}
          title={DIGEST_COPY.title}
          icon={DIGEST_COPY.icon}
          description={DIGEST_COPY.description}
          badge={DIGEST_COPY.badge}
          badgeTone="neutral"
          enabled={digest.subscribed}
          busy={digestBusy}
          canAdminister={!!digest.membershipId}
          onToggle={toggleDigest}
        />
      </AutomationSection>
    </div>
  );
};

AutomationRules.propTypes = {
  team: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) })
    .isRequired,
  canAdminister: PropTypes.bool,
  rulesAvailable: PropTypes.bool,
  automationEnabled: PropTypes.bool,
  autoRefundAvailable: PropTypes.bool,
  signalAction: PropTypes.string,
  digest: PropTypes.shape({
    membershipId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    subscribed: PropTypes.bool,
  }),
  onDigestChange: PropTypes.func,
  onBack: PropTypes.func.isRequired,
};

AutomationRules.defaultProps = {
  canAdminister: false,
  rulesAvailable: false,
  automationEnabled: false,
  autoRefundAvailable: false,
  signalAction: 'flag',
  digest: { membershipId: null, subscribed: false },
  onDigestChange: () => {},
};

export default AutomationRules;
